import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, UserPlus, AlertCircle, CheckCircle2, Image, Zap, Lock, BarChart3, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '@/contexts/AuthContext';
import { authAdapter } from '@/api/authAdapter';
import { API_BASE_URL } from '@/api/apiConfig';
import { buildKakaoAuthorizeUrl } from '@/lib/socialAuth';
import { TermsModal } from './TermsModal';
import { PrivacyModal } from './PrivacyModal';
import { SocialConsentModal, SocialConsentAgreements } from './SocialConsentModal';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    userId: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    phone: '',
    preferredLanguage: 'ko' as 'ko' | 'en',
  });

  const [agreements, setAgreements] = useState({
    agreeTerms: false,
    agreePrivacy: false,
    agreeMarketing: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userIdChecked, setUserIdChecked] = useState(false);
  const [userIdAvailable, setUserIdAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // 로그인 페이지에서 소셜 로그인 시도 후 신규 유저로 감지되면 자동으로 동의 모달 표시
  useEffect(() => {
    const autoConsent = (location.state as any)?.autoOpenConsent as string | undefined;
    if (autoConsent === 'naver' || autoConsent === 'kakao') {
      setSocialConsentProvider(autoConsent);
      setShowSocialConsent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시 1회만 실행 (location.state는 초기값 고정)

  // 소셜 회원가입 동의 상태
  const [showSocialConsent, setShowSocialConsent] = useState(false);
  const [socialConsentProvider, setSocialConsentProvider] = useState<'google' | 'naver' | 'kakao'>('google');
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState<string | null>(null);

  // 이메일 인증 상태
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailResendCountdown, setEmailResendCountdown] = useState(0);

  const getPasswordStrength = (password: string): PasswordStrength => {
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[@$!%*#?&]/.test(password)) score++;

    const strengths = [
      { score: 1, label: '매우 약함', color: 'bg-red-600' },
      { score: 2, label: '약함', color: 'bg-red-500' },
      { score: 3, label: '보통', color: 'bg-yellow-500' },
      { score: 4, label: '강함', color: 'bg-green-500' },
      { score: 5, label: '매우 강함', color: 'bg-green-600' },
    ];

    return strengths.find(s => s.score === score) || { score: 0, label: '매우 약함', color: 'bg-red-600' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const passwordRequirements = [
    { met: formData.password.length >= 10, text: '10자 이상' },
    { met: /[A-Z]/.test(formData.password), text: '대문자 포함' },
    { met: /[a-z]/.test(formData.password), text: '소문자 포함' },
    { met: /\d/.test(formData.password), text: '숫자 포함' },
    { met: /[@$!%*#?&]/.test(formData.password), text: '특수문자 포함 (@$!%*#?&)' },
  ];


  // 인증번호 발송
  const sendEmailVerification = async () => {
    if (!formData.email) {
      setEmailError('이메일을 입력해주세요.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailError('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    setEmailSending(true);
    setEmailError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/email-verification/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setEmailError(data.message || '인증번호 발송에 실패했습니다.');
        return;
      }
      setEmailCodeSent(true);
      setEmailCode('');
      setEmailVerified(false);
      // 60초 재발송 방지 카운트다운
      setEmailResendCountdown(60);
      const timer = setInterval(() => {
        setEmailResendCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch {
      setEmailError('인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setEmailSending(false);
    }
  };

  // 인증번호 확인
  const verifyEmailCode = async () => {
    if (!emailCode.trim()) {
      setEmailError('인증번호를 입력해주세요.');
      return;
    }
    setEmailVerifying(true);
    setEmailError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/email-verification/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, code: emailCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setEmailError(data.message || '인증번호가 올바르지 않습니다.');
        return;
      }
      setEmailVerified(true);
      setEmailError(null);
    } catch {
      setEmailError('인증 확인 중 오류가 발생했습니다.');
    } finally {
      setEmailVerifying(false);
    }
  };

  const checkUserId = async () => {
    if (!formData.userId) {
      setError('아이디를 입력해주세요.');
      return;
    }

    if (formData.userId.length < 4 || formData.userId.length > 20) {
      setError('아이디는 4-20자로 입력해주세요.');
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(formData.userId)) {
      setError('아이디는 영문과 숫자만 사용 가능합니다.');
      return;
    }

    setChecking(true);
    setError(null);

    try {
      console.log('[Signup] 아이디 확인 시작:', formData.userId);
      const response = await authAdapter.checkUsername(formData.userId);
      console.log('[Signup] 아이디 확인 응답:', response);
      
      if (response.available) {
        setUserIdAvailable(true);
        setUserIdChecked(true);
        setError(null);
      } else {
        setUserIdAvailable(false);
        setError('이미 사용 중인 아이디입니다.');
      }
    } catch (err: any) {
      console.error('[Signup] 아이디 확인 에러:', err);
      setError(err.message || '아이디 확인 중 오류가 발생했습니다.');
    } finally {
      setChecking(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('Google 회원가입에 실패했습니다.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 기존 회원이면 바로 로그인 처리
      await login(credentialResponse.credential, '', false, 'google');
      navigate('/dashboard');
    } catch (err: any) {
      // 신규 유저인 경우만 동의 화면 표시
      if (err.code === 'SOCIAL_NEW_USER' || err.code === 'SOCIAL_SIGNUP_PENDING' || err.message?.startsWith('SOCIAL_SIGNUP_PENDING|')) {
        setPendingGoogleCredential(credentialResponse.credential);
        setSocialConsentProvider('google');
        setShowSocialConsent(true);
      } else {
        setError(err.message || 'Google 회원가입에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleConsentConfirm = async (agreements: SocialConsentAgreements) => {
    if (!pendingGoogleCredential) return;
    setShowSocialConsent(false);
    setLoading(true);
    setError(null);

    try {
      await login(pendingGoogleCredential, '', false, 'google', agreements);
      navigate('/dashboard');
    } catch (err: any) {
      if (err.code === 'SOCIAL_SIGNUP_PENDING' || err.message?.startsWith('SOCIAL_SIGNUP_PENDING|')) {
        const rawMsg = err.message || '';
        const userMsg = rawMsg.startsWith('SOCIAL_SIGNUP_PENDING|')
          ? rawMsg.substring('SOCIAL_SIGNUP_PENDING|'.length)
          : '회원가입 신청이 완료되었습니다. 관리자 승인 후 이용 가능합니다.';
        const pendingEmail = err.data?.email || err.data?.data?.email;
        navigate('/pending-approval', { state: { message: userMsg, email: pendingEmail }, replace: true });
        return;
      }
      setError(err.message || 'Google 회원가입에 실패했습니다.');
    } finally {
      setLoading(false);
      setPendingGoogleCredential(null);
    }
  };

  const handleGoogleError = () => {
    setError('Google 회원가입에 실패했습니다.');
  };

  const handleNaverSignup = () => {
    setError(null);
    // 기존 회원 여부를 먼저 확인하기 위해 바로 OAuth 진행 (로그인 페이지와 동일)
    // 신규 유저인 경우 NaverCallback이 autoOpenConsent와 함께 회원가입 페이지로 돌아옴
    const clientId = process.env.REACT_APP_NAVER_CLIENT_ID || '';
    const redirectUri = `${window.location.origin}/auth/naver/callback`;
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('naverLoginState', state);
    window.location.href = `https://nid.naver.com/oauth2.0/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&state=${state}`;
  };

  const handleKakaoSignup = () => {
    setError(null);
    // 기존 회원 여부를 먼저 확인하기 위해 바로 OAuth 진행 (로그인 페이지와 동일)
    // 신규 유저인 경우 KakaoCallback이 autoOpenConsent와 함께 회원가입 페이지로 돌아옴
    window.location.href = buildKakaoAuthorizeUrl();
  };

  const handleSocialConsentConfirm = (agreements: SocialConsentAgreements) => {
    setShowSocialConsent(false);
    // 동의 정보를 sessionStorage에 저장 (OAuth redirect 후 callback에서 읽음)
    sessionStorage.setItem('socialPreOAuthConsent', JSON.stringify({
      agreeTerms: agreements.agreeTerms,
      agreePrivacy: agreements.agreePrivacy,
      agreeMarketing: agreements.agreeMarketing,
    }));

    if (socialConsentProvider === 'naver') {
      const clientId = process.env.REACT_APP_NAVER_CLIENT_ID || '';
      const redirectUri = `${window.location.origin}/auth/naver/callback`;
      const state = Math.random().toString(36).substring(7);
      sessionStorage.setItem('naverLoginState', state);
      sessionStorage.setItem('naverSignupContext', 'true');
      window.location.href = `https://nid.naver.com/oauth2.0/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&state=${state}`;
    } else if (socialConsentProvider === 'kakao') {
      sessionStorage.setItem('kakaoSignupContext', 'true');
      window.location.href = buildKakaoAuthorizeUrl();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!userIdChecked || !userIdAvailable) {
      setError('아이디 중복 확인을 해주세요.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (passwordStrength.score < 2) {
      setError('비밀번호가 너무 약합니다. 더 강한 비밀번호를 사용해주세요.');
      return;
    }

    if (!agreements.agreeTerms || !agreements.agreePrivacy) {
      setError('필수 약관에 동의해주세요.');
      return;
    }

    if (!emailVerified) {
      setError('이메일 인증을 완료해주세요.');
      return;
    }

    setLoading(true);

    try {
      console.log('[Signup] 회원가입 시작:', formData);
      const response = await authAdapter.signup({
        userId: formData.userId,
        password: formData.password,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        preferredLanguage: formData.preferredLanguage,
        agreeTerms: agreements.agreeTerms,
        agreePrivacy: agreements.agreePrivacy,
        agreeMarketing: agreements.agreeMarketing,
      });

      console.log('[Signup] 회원가입 응답:', response);
      if (response.success) {
        setSuccess(true);
      } else {
        setError('회원가입에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('[Signup] 회원가입 에러:', err);
      setError(err.message || '회원가입에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl">회원가입 완료</CardTitle>
            <CardDescription className="mt-2">
              회원가입이 성공적으로 완료되었습니다.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full">
              로그인하러 가기
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* 좌측: 서비스 소개 */}
        <div className="hidden lg:flex flex-col justify-start items-center pt-20 px-12 pb-12 bg-gradient-to-br from-primary to-blue-700 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full -ml-48 -mb-48"></div>

          <div className="relative z-10 max-w-md">
            <div className="mb-8 flex items-center justify-center">
              <div className="bg-white bg-opacity-40 p-4 rounded-full backdrop-blur-sm shadow-lg">
                <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-16 h-16 rounded-full" />
              </div>
            </div>

            <Link to="/" className="block">
              <h1 className="text-5xl font-bold text-center mb-4 hover:text-blue-200 transition-colors cursor-pointer">
                ALT Generation
              </h1>
            </Link>
            <p className="text-xl text-blue-100 text-center mb-12">
              AI 기반 이미지 ALT 텍스트 자동 생성 시스템
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">빠른 생성</h3>
                  <p className="text-blue-100">
                    AI 기술을 이용한 초고속 ALT 텍스트 생성
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Lock className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">안전한 관리</h3>
                  <p className="text-blue-100">
                    엔터프라이즈급 보안으로 데이터 보호
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">상세 분석</h3>
                  <p className="text-blue-100">
                    생성된 ALT 텍스트의 품질 및 사용 현황 분석
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-white border-opacity-20">
              <p className="text-blue-100 text-center text-sm">
                웹사이트 크롤링, 이미지 URL, 이미지 업로드 등 <br /> 다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
              </p>
            </div>
          </div>
        </div>

        {/* 우측: 회원가입 폼 */}
        <div className="flex flex-col justify-center items-center p-6 md:p-12">
          <div className="w-full max-w-md mb-3">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              홈으로
            </Link>
          </div>
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="text-center sticky top-0 bg-white z-10">
              <div className="flex justify-center mb-4 lg:hidden">
                <div className="bg-white p-3 rounded-lg shadow-md">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-12 h-12 rounded-full" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold">회원가입</CardTitle>
              <CardDescription className="text-base mt-2">
                ALT Generation 시스템 회원가입
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 아이디 */}
                <div>
                  <label htmlFor="userId" className="block text-sm font-medium mb-2">
                    아이디 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      id="userId"
                      type="text"
                      value={formData.userId}
                      onChange={(e) => {
                        setFormData({ ...formData, userId: e.target.value });
                        setUserIdChecked(false);
                        setUserIdAvailable(false);
                      }}
                      placeholder="4-20자, 영문/숫자"
                      disabled={loading}
                      required
                      className="flex-1 h-10"
                    />
                    <Button
                      type="button"
                      onClick={checkUserId}
                      disabled={checking || loading || !formData.userId}
                      variant="outline"
                      size="sm"
                    >
                      {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : '확인'}
                    </Button>
                  </div>
                  {userIdChecked && userIdAvailable && (
                    <p className="text-sm text-green-600 mt-1">✓ 사용 가능한 아이디입니다</p>
                  )}
                </div>

                {/* 비밀번호 */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="비밀번호 입력"
                      disabled={loading}
                      required
                      className="h-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1">
                          <div
                            className={`h-full rounded-full transition-all ${passwordStrength.color}`}
                            style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium text-gray-600">{passwordStrength.label}</span>
                      </div>
                      <div className="text-xs space-y-1">
                        {passwordRequirements.map((req, idx) => (
                          <div key={idx} className={req.met ? 'text-green-600' : 'text-gray-400'}>
                            {req.met ? '✓' : '○'} {req.text}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 비밀번호 확인 */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                    비밀번호 확인 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="비밀번호 확인"
                      disabled={loading}
                      required
                      className="h-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-sm text-red-600 mt-1">비밀번호가 일치하지 않습니다</p>
                  )}
                </div>

                {/* 이름 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="이름"
                    disabled={loading}
                    required
                    className="h-10"
                  />
                </div>

                {/* 이메일 */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    이메일 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        setEmailCodeSent(false);
                        setEmailVerified(false);
                        setEmailCode('');
                        setEmailError(null);
                      }}
                      placeholder="이메일"
                      disabled={loading || emailVerified}
                      required
                      className="flex-1 h-10"
                    />
                    <Button
                      type="button"
                      onClick={sendEmailVerification}
                      disabled={emailSending || loading || emailVerified || !formData.email || emailResendCountdown > 0}
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      {emailSending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : emailVerified ? (
                        '인증완료'
                      ) : emailCodeSent ? (
                        emailResendCountdown > 0 ? `재발송 (${emailResendCountdown}s)` : '재발송'
                      ) : (
                        '인증번호 발송'
                      )}
                    </Button>
                  </div>
                  {emailVerified && (
                    <p className="text-sm text-green-600 mt-1">✓ 이메일 인증이 완료되었습니다</p>
                  )}
                  {emailCodeSent && !emailVerified && (
                    <div className="mt-2 space-y-2">
                      <p className="text-xs text-blue-700">📧 {formData.email} 으로 인증번호를 발송했습니다. (5분 이내 입력)</p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={emailCode}
                          onChange={(e) => setEmailCode(e.target.value)}
                          placeholder="인증번호 6자리 입력"
                          maxLength={6}
                          disabled={emailVerifying || loading}
                          className="flex-1 h-10"
                        />
                        <Button
                          type="button"
                          onClick={verifyEmailCode}
                          disabled={emailVerifying || loading || !emailCode.trim()}
                          size="sm"
                          className="whitespace-nowrap"
                        >
                          {emailVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : '확인'}
                        </Button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    탈퇴한 계정은 동일 이메일로 30일 동안 재가입할 수 없습니다.
                  </p>
                  {emailError && (
                    <p className="text-sm text-red-600 mt-1">{emailError}</p>
                  )}
                </div>

                {/* 전화번호 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-2">
                    전화번호
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="전화번호 (선택)"
                    disabled={loading}
                    className="h-10"
                  />
                </div>

                {/* 약관 동의 */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={agreements.agreeTerms}
                      onChange={(e) => setAgreements({ ...agreements, agreeTerms: e.target.checked })}
                      disabled={loading}
                      className="w-4 h-4 mt-1 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-primary hover:underline font-medium"
                      >
                        이용약관
                      </button>
                      에 동의합니다 <span className="text-red-500">*</span>
                    </span>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={agreements.agreePrivacy}
                      onChange={(e) => setAgreements({ ...agreements, agreePrivacy: e.target.checked })}
                      disabled={loading}
                      className="w-4 h-4 mt-1 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-primary hover:underline font-medium"
                      >
                        개인정보 처리방침
                      </button>
                      에 동의합니다 <span className="text-red-500">*</span>
                    </span>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreements.agreeMarketing}
                      onChange={(e) => setAgreements({ ...agreements, agreeMarketing: e.target.checked })}
                      disabled={loading}
                      className="w-4 h-4 mt-1 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">
                      마케팅 정보 수신에 동의합니다 (선택)
                    </span>
                  </label>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full h-10 text-base font-medium mt-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      가입 중...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      회원가입
                    </>
                  )}
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">또는</span>
                  </div>
                </div>

                <div className="w-full space-y-3">
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      text="signup_with"
                      size="large"
                    />
                  </div>

                  <div className="flex gap-3 justify-center">
                    <Button
                      type="button"
                      onClick={handleNaverSignup}
                      disabled={loading}
                      className="h-12 px-6 bg-[#2DB400] hover:bg-[#25a000] text-white font-medium flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16.273 12.845L7.727 0h-7.727v24h6.545v-11.155l8.546 11.155h7.727v-24h-6.545v12.845z"/>
                      </svg>
                      네이버
                    </Button>
                    {/* 카카오 회원가입 */}
                    <Button
                      type="button"
                      onClick={handleKakaoSignup}
                      disabled={loading}
                      className="h-12 px-6 bg-[#FEE500] hover:bg-[#F0D800] text-[#3A1D1D] font-medium flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#3A1D1D">
                        <path d="M12 3C6.477 3 2 6.477 2 10.737c0 2.738 1.748 5.139 4.38 6.555-.193.718-1.261 4.694-1.298 4.951 0 0-.018.157.082.218.099.061.214.013.214.013.28-.039 3.233-2.121 3.786-2.514A11.82 11.82 0 0012 20.474c5.523 0 10-3.477 10-7.737S17.523 3 12 3z"/>
                      </svg>
                      카카오
                    </Button>
                  </div>
                </div>

                <div className="text-center text-sm mt-4">
                  <span className="text-gray-600">이미 계정이 있으신가요? </span>
                  <Link to="/login" className="text-primary hover:underline font-semibold">
                    로그인
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* 모바일용 서비스 소개 */}
          <div className="mt-8 lg:hidden w-full max-w-md pb-8">
            <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-6 text-white">
              <div className="text-center mb-5">
                <h2 className="text-lg font-bold">ALT Generation</h2>
                <p className="text-blue-100 text-sm mt-1">AI 기반 이미지 ALT 텍스트 자동 생성 시스템</p>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">빠른 생성</p>
                    <p className="text-blue-100 text-xs mt-0.5">AI 기술을 이용한 초고속 ALT 텍스트 생성</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <Lock className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">안전한 관리</p>
                    <p className="text-blue-100 text-xs mt-0.5">엔터프라이즈급 보안으로 데이터 보호</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">상세 분석</p>
                    <p className="text-blue-100 text-xs mt-0.5">생성된 ALT 텍스트의 품질 및 사용 현황 분석</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-white/20">
                <p className="text-blue-100 text-center text-xs">
                  웹사이트 크롤링, 이미지 URL, 이미지 업로드 등<br />다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 약관 모달 */}
      {showTermsModal && <TermsModal open={showTermsModal} onOpenChange={setShowTermsModal} />}
      {showPrivacyModal && <PrivacyModal open={showPrivacyModal} onOpenChange={setShowPrivacyModal} />}

      {/* 소셜 회원가입 동의 모달 */}
      <SocialConsentModal
        open={showSocialConsent}
        onOpenChange={(open) => {
          setShowSocialConsent(open);
          if (!open) setPendingGoogleCredential(null);
        }}
        provider={socialConsentProvider}
        onConfirm={socialConsentProvider === 'google' ? handleGoogleConsentConfirm : handleSocialConsentConfirm}
      />
    </div>
  );
}

export default Signup;
